import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse as _AxiosResponse,
  AxiosResponseTransformer,
} from "axios";
import produce from "immer";
import { getXSRFCookieVal } from "src/utils/cookies";
import { getLocalStorageLoginToken } from "src/utils/localStorageWorker";
import { X_CSRF_TOKEN } from "../config/axios";
import flowioast from "../utils/flowioast";
import { removeLeadingSlash } from "../utils/global";
import { AError } from "./types";

export interface ResponseWithError<T> extends _AxiosResponse<T> {
  errors?: AError;
}

type RequestConfig = AxiosRequestConfig;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AxiosResponse<T extends any> = Promise<ResponseWithError<T>>;

const AxiosResponse = Promise; // unused, ale nutné pro TS - https://github.com/microsoft/TypeScript/issues/12776

// Zajišťuje, že v případě erroru doputuje response error kam má, a nevyhodí
// se jiným errorem, pocházejícím z parseru
// POZOR: transofrmResponse nefunguje na striny, jelikož není možné zjistit,
// jestli request dopadl jako error
// TODO: Udělat to nějak ještě líp, aby to fungovalo i na stringy (asi pres interceptor)
const transformResponseWrapper = (transformers: AxiosResponseTransformer[]) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (rawResponse: any) => {
      try {
        const parsed = JSON.parse(rawResponse);
        if (typeof parsed === "string") return parsed;

        const transformed = transformers.reduce(
          (acc, transformer) => transformer(acc),
          parsed
        );

        return transformed;
      } catch (e) {
        return rawResponse;
      }
    };
  } catch (e) {
    // at vim odkud vitr vane, kdyz se to stane
    // TODO: POZOR, nechytá mi to chyby a probublají rovnou nahoru - třeba zprovoznit
    flowioast.error.fromIntlBase("relation_table.fetch.singular", e);
    throw new Error("WTF!!!");
  }
};

function transformRequestConfig(config: RequestConfig): RequestConfig {
  return produce(config, (draft) => {
    draft.url = removeLeadingSlash(config.url);

    draft.headers = {
      ...draft.headers,
      // todo: Authorization
      Authorization: getLocalStorageLoginToken() ?? "",
      [X_CSRF_TOKEN]: getXSRFCookieVal(),
    };

    if (config.transformResponse) {
      const transformers = [config.transformResponse].flat();
      draft.transformResponse = transformResponseWrapper(transformers);
    }
  });
}

async function axiosWrapper<T>(config: RequestConfig): AxiosResponse<T>;
async function axiosWrapper<T>(
  url: string,
  config: RequestConfig
): AxiosResponse<T>;
async function axiosWrapper<T>(
  p1: string | RequestConfig,
  p2?: RequestConfig
): AxiosResponse<T> {
  const config = typeof p1 === "string" ? { ...p2, url: p1 } : p1;

  const processedConfig = transformRequestConfig(config);

  try {
    return axios(processedConfig);
  } catch (e) {
    if ((e as AxiosError).response?.data === "NO_LICENSE") {
      flowioast.error.noStack.fromIntlBase("license.invalid_license.singular");
    }
    return Promise.reject(e);
  }
}

export default axiosWrapper;
